'use client';

import * as React from 'react';
import {
    PhoneInput,
    type CountryIso2,
    type CountrySelectorProps,
    type PhoneInputRefType,
} from 'react-international-phone';
import { RequiredMarker } from '@/components/required-fields-legend';
import { inputFieldVariants, type InputFieldVariantProps } from '@/components/ui/field-variants';
import { Label } from '@/components/ui/label';
import { useAllowedPhoneCountries } from '@/hooks/use-phone-input-config';
import { fieldDescribedByIds } from '@/lib/field-described-by';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

/** ISO 3166-1 alpha-2 country code (lowercase, as used by react-international-phone). */
export type Country = CountryIso2;

export type CustomPhoneInputProps = {
    id: string;
    name: string;
    label: React.ReactNode;
    value?: string;
    onChange?: (value: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    readOnly?: boolean;
    autoComplete?: string;
    errorMessage?: string;
    errorId: string;
    hintId?: string;
    hintText?: string;
    defaultCountry?: Country | Uppercase<Country>;
    className?: string;
    labelClassName?: string;
    paddingClassName?: string;
    /** When set, appends a visual asterisk after the label. */
    required?: boolean;
    /**
     * Dial codes (`+39`) the country selector may offer.
     * Omit it, or pass an empty list, to keep every prefix. Registration and profile pass the whitelist.
     */
    allowedPrefixes?: string | readonly string[];
} & InputFieldVariantProps;

const CustomPhoneInput = React.forwardRef<HTMLInputElement, CustomPhoneInputProps>(function CustomPhoneInput(
    {
        id,
        name,
        label,
        value,
        onChange,
        onBlur,
        disabled,
        readOnly,
        autoComplete = 'tel',
        errorMessage,
        errorId,
        hintId,
        hintText,
        className,
        labelClassName,
        variant = 'floating',
        paddingClassName = 'pb-4',
        required,
        allowedPrefixes,
    },
    ref
) {
    const { countries, defaultCountry } = useAllowedPhoneCountries(allowedPrefixes);
    const { t } = useTranslation('registrationPage');
    const hasError = Boolean(errorMessage);
    const describedBy = fieldDescribedByIds({ errorId, hasError, hintId });
    const selectorContainerRef = React.useRef<HTMLDivElement>(null);

    // The library renders the options list without an accessible name: give it one (combobox pattern).
    const countryListLabel = t('fields.phone.countryListAriaLabel');
    React.useEffect(() => {
        selectorContainerRef.current?.querySelector('[role="listbox"]')?.setAttribute('aria-label', countryListLabel);
    }, [countryListLabel]);

    // Choosing a prefix must not move focus to the phone field (WCAG 3.2.2): keep it on the country
    // selector, like a native select. The library hides the focused listbox on select/Escape, which would
    // otherwise drop focus to <body>.
    React.useEffect(() => {
        const container = selectorContainerRef.current;
        if (!container) {
            return;
        }
        const returnFocusToSelector = () => {
            requestAnimationFrame(() => {
                container.querySelector<HTMLButtonElement>('.react-international-phone-country-selector-button')?.focus();
            });
        };
        const onClick = (event: MouseEvent) => {
            if (event.target instanceof Element && event.target.closest('[role="option"]')) {
                returnFocusToSelector();
            }
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (
                (event.key === 'Enter' || event.key === 'Escape') &&
                event.target instanceof Element &&
                event.target.closest('[role="listbox"]')
            ) {
                returnFocusToSelector();
            }
        };
        container.addEventListener('click', onClick);
        container.addEventListener('keydown', onKeyDown);
        return () => {
            container.removeEventListener('click', onClick);
            container.removeEventListener('keydown', onKeyDown);
        };
    }, []);

    return (
        <div className={cn('flex w-full min-w-0 flex-col mb-4', className)}>
            <div className={cn('relative', paddingClassName)}>
                <div className="relative">
                    <div ref={selectorContainerRef} className={cn(inputFieldVariants({ variant }), 'phone-input w-full')}>
                        <PhoneInput
                            ref={ref as React.Ref<PhoneInputRefType>}
                            defaultCountry={defaultCountry}
                            countries={countries}
                            value={value}
                            onChange={onChange}
                            disabled={disabled}
                            forceDialCode={true}
                            disableFocusAfterCountrySelect
                            name={name}
                            className="flex min-h-0 min-w-0 flex-1"
                            countrySelectorStyleProps={
                                {
                                    renderButtonWrapper: ({
                                        children,
                                        rootProps,
                                    }: Parameters<NonNullable<CountrySelectorProps['renderButtonWrapper']>>[0]) => (
                                        <button
                                            {...rootProps}
                                            // The selected country is part of the accessible name: a title would
                                            // expose it again as (non-appropriate) description.
                                            title={undefined}
                                            type="button"
                                            disabled={disabled || readOnly}
                                            aria-label={
                                                rootProps.title
                                                    ? t('fields.phone.countrySelectorAriaLabelWithValue', {
                                                          country: rootProps.title,
                                                      })
                                                    : t('fields.phone.countrySelectorAriaLabel')
                                            }
                                            className="react-international-phone-country-selector-button">
                                            {React.isValidElement<{ 'aria-hidden'?: boolean }>(children)
                                                ? React.cloneElement(children, { 'aria-hidden': true })
                                                : children}
                                        </button>
                                    ),
                                } as unknown as NonNullable<React.ComponentProps<typeof PhoneInput>['countrySelectorStyleProps']>
                            }
                            inputProps={{
                                id,
                                onBlur,
                                autoComplete,
                                readOnly,
                                required,
                                'aria-invalid': hasError,
                                'aria-describedby': describedBy,
                                placeholder: ' ',
                            }}
                            inputClassName={cn(
                                'body-copy-l min-w-0 flex-1 border-0 bg-transparent text-[var(--field-text-color)] shadow-none outline-none disabled:cursor-not-allowed disabled:opacity-50'
                            )}
                        />
                    </div>
                    <Label htmlFor={id} variant={variant} data-error={hasError} className={labelClassName}>
                        {label}
                        {required ? <RequiredMarker /> : null}
                    </Label>
                    {hintId && hintText && <span id={hintId} className="sr-only">
                        {hintText}
                    </span>}
                </div>
                <p
                    id={errorId}
                    className={cn(
                        'body-copy-s absolute bottom-0 left-0 right-0 whitespace-nowrap text-destructive',
                        !hasError && 'text-transparent'
                    )}>
                    {hasError && errorMessage}
                </p>
            </div>
        </div>
    );
});

export { CustomPhoneInput };
